import { useState } from "react";
import DislikeIcon from "./dislikeIcon";
import LikeIcon from "./likeIcon";
import { useAppDispatch, useAppSelector } from "redux-share/hooks";
import { API, apiCaller } from "../../../../../services";
import { setVideoSettings } from "redux-share/slices/userSlice";
import { createVideoSettings } from "redux-share/actions/user.actions";
import { likeDislikeLog, sendFluentLog } from "../../../../../logs";
import { conditions, getUUID, unitization } from "helpers";
import { ConditionName } from "interfaces";
import { shallowEqual } from "react-redux";

const LikeDisLike = () => {
    const dispatch = useAppDispatch();
    const { details } = useAppSelector(
        ({ videoData: { details, isComplexPortrait, isComplexDesktop, videoElementSizeInfo, params } }) => ({
            details,
            isComplexPortrait,
            isComplexDesktop,
            videoElementSizeInfo,
            params
        }),
        shallowEqual
    );
    const { videoSettings } = useAppSelector(({ user: { videoSettings, settings } }) => ({ videoSettings, settings }), shallowEqual);
    useAppSelector(({ controller: { showMiniPlayer } }) => ({ showMiniPlayer }), shallowEqual);

    const [likeCount, setLikeCount] = useState<number>(details.like ? details.like : 0);
    const [disable, setDisable] = useState<boolean>(false);

    const handleLikeDislike = async (type: string) => {
        setDisable(true);
        if (videoSettings.is_like === null) {
            if (type === "like") {
                await apiCaller({ url: API().like, data: { uuid: getUUID(), user_id: !!details.user_id ? details.user_id : undefined } });
                dispatch(setVideoSettings({ ...videoSettings, is_like: true }));
                createVideoSettings({ ...videoSettings, is_like: true });
                sendFluentLog(likeDislikeLog("like"));
                setLikeCount((prev: number) => prev + 1);
                setDisable(false);
            } else {
                await apiCaller({ url: API().dislike, data: { uuid: getUUID(), user_id: !!details.user_id ? details.user_id : undefined } });
                dispatch(setVideoSettings({ ...videoSettings, is_like: false }));
                createVideoSettings({ ...videoSettings, is_like: false });
                sendFluentLog(likeDislikeLog("dislike"));
                setDisable(false);
            }
        } else if (videoSettings.is_like === true) {
            if (type === "like") {
                await apiCaller({ url: API().unlike, data: { uuid: getUUID(), user_id: !!details.user_id ? details.user_id : undefined } });
                dispatch(setVideoSettings({ ...videoSettings, is_like: null }));
                createVideoSettings({ ...videoSettings, is_like: null });
                sendFluentLog(likeDislikeLog("unlike"));
                setLikeCount((prev: number) => prev - 1);
                setDisable(false);
            } else {
                await apiCaller({ url: API().dislike, data: { uuid: getUUID(), user_id: !!details.user_id ? details.user_id : undefined } });
                dispatch(setVideoSettings({ ...videoSettings, is_like: false }));
                createVideoSettings({ ...videoSettings, is_like: false });
                sendFluentLog(likeDislikeLog("dislike"));
                setLikeCount((prev: number) => prev - 1);
                setDisable(false);
            }
        } else if (videoSettings.is_like === false) {
            if (type === "like") {
                await apiCaller({ url: API().like, data: { uuid: getUUID(), user_id: !!details.user_id ? details.user_id : undefined } });
                dispatch(setVideoSettings({ ...videoSettings, is_like: true }));
                createVideoSettings({ ...videoSettings, is_like: true });
                sendFluentLog(likeDislikeLog("like"));
                setLikeCount((prev: number) => prev + 1);
                setDisable(false);
            } else {
                await apiCaller({ url: API().undislike, data: { uuid: getUUID(), user_id: !!details.user_id ? details.user_id : undefined } });
                dispatch(setVideoSettings({ ...videoSettings, is_like: null }));
                createVideoSettings({ ...videoSettings, is_like: null });
                sendFluentLog(likeDislikeLog("unDislike"));
                setDisable(false);
            }
        }
    };

    const likeStyles = () => {
        let styles = "";
        if (videoSettings.is_like === true) {
            if (conditions(ConditionName.isComplexDesktop) || conditions(ConditionName.isComplexPortrait))
                styles = `${styles} pl-fill-secondary-700 dark:pl-fill-dark-5`;
            else styles = `${styles} pl-fill-dark-5`;
        } else {
            if (conditions(ConditionName.isComplexDesktop)) styles = `${styles} pl-stroke-secondary-700 dark:pl-stroke-secondary-50`;
            else if (conditions(ConditionName.isComplexPortrait)) styles = `${styles} pl-stroke-gray-800 dark:pl-stroke-dark-5`;
            else styles = `${styles} pl-stroke-dark-5`;
        }
        return styles;
    };

    const dislikeStyles = () => {
        let styles = "";
        if (videoSettings.is_like === false) {
            if (conditions(ConditionName.isComplexDesktop) || conditions(ConditionName.isComplexPortrait))
                styles = `${styles} pl-fill-secondary-700 dark:pl-fill-dark-5`;
            else styles = `${styles} pl-fill-dark-5`;
        } else {
            if (conditions(ConditionName.isComplexDesktop)) styles = `${styles} pl-stroke-secondary-700 dark:pl-stroke-secondary-50`;
            else if (conditions(ConditionName.isComplexPortrait)) styles = `${styles} pl-stroke-gray-800 dark:pl-stroke-dark-5`;
            else styles = `${styles} pl-stroke-dark-5`;
        }
        return styles;
    };

    const textStyles = () => {
        let styles = "";
        if (conditions(ConditionName.isComplexPortrait)) styles = `pl-text-z-gray-500 dark:pl-text-secondary-50`;
        else if (conditions(ConditionName.isComplexDesktop)) styles = `pl-text-secondary-700 dark:pl-text-dark-5`;
        else styles = `pl-text-dark-5`;
        return styles;
    };

    return (
        <div
            className={`pl-relative pl-flex pl-items-center ${
                conditions(ConditionName.isComplexDesktop) || conditions(ConditionName.isComplexPortrait)
                    ? "pl-bg-gray-100 dark:pl-bg-dark-3"
                    : "pl-border pl-border-dark-5"
            } pl-rounded-[80px]`}
            onDoubleClick={(event: any) => event.stopPropagation()}
        >
            <div
                className={`pl-cursor-pointer pl-px-2 pl-rounded-r-[80px] pl-py-[6px] ${
                    conditions(ConditionName.isComplexDesktop) &&
                    "pl-transition-colors pl-duration-500 hover:pl-bg-gray-300 dark:hover:pl-bg-dark-10/[0.9]"
                }`}
            >
                <DislikeIcon
                    className={`${dislikeStyles()}`}
                    onClick={() => !disable && handleLikeDislike("dislike")}
                    onDoubleClick={(event: any) => event.stopPropagation()}
                    isDislike={videoSettings.is_like === false}
                />
            </div>
            <div
                className={`pl-absolute pl-w-[1px] pl-h-4 ${
                    conditions(ConditionName.isComplexDesktop) || conditions(ConditionName.isComplexPortrait)
                        ? "pl-bg-z-gray-700 dark:pl-bg-dark-4"
                        : "pl-bg-dark-5"
                } pl-relative ${conditions(ConditionName.isMiniPlayer) ? "pl-bottom-[1px]" : "pl-bottom-[1px]"}`}
            ></div>
            <div
                className={`pl-flex pl-items-center pl-gap-2 pl-cursor-pointer pl-px-2 ${
                    conditions(ConditionName.isMiniPlayer) ? "pl-py-[3px]" : "pl-py-[6px]"
                } pl-rounded-l-[80px] ${
                    conditions(ConditionName.isComplexDesktop) &&
                    "pl-transition-colors pl-duration-500 hover:pl-bg-gray-300 dark:hover:pl-bg-dark-10/[0.9]"
                }`}
                onClick={() => !disable && handleLikeDislike("like")}
            >
                <LikeIcon className={`${likeStyles()}`} isLike={videoSettings.is_like === true} />
                <div className={`pl-flex pl-items-center pl-gap-0.5 ${textStyles()} pl-text-[14px] pl-font-bold pl-relative pl-top-[1px]`}>
                    <div>{unitization(likeCount).unit}</div>
                    <div>{unitization(likeCount).value}</div>
                </div>
            </div>
        </div>
    );
};

export default LikeDisLike;
